import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Search, 
  Filter, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ChevronRight,
  X
} from 'lucide-react';
import api from '../services/api';

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      // Auth context attaches the JWT cookie; backend scopes by company_id.
      const data = await api.getTickets(null, statusFilter === 'all' ? null : statusFilter);
      setTickets(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading tickets:', error);
      setTickets([]);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'open': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const filteredTickets = tickets.filter(ticket =>
    ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.requester.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-gray-600">Loading tickets...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">All Tickets</h2>
          <p className="text-gray-600 mt-1">Manage and track all support tickets</p>
        </div>
        <Button className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white">
          Export Report
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 border-gray-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 mb-1">Total Tickets</div>
            <div className="text-3xl font-bold text-gray-900">{tickets.length}</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 mb-1">Open</div>
            <div className="text-3xl font-bold text-yellow-600">
              {tickets.filter(t => t.status === 'open').length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 mb-1">In Progress</div>
            <div className="text-3xl font-bold text-blue-600">
              {tickets.filter(t => t.status === 'in_progress').length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="text-sm text-gray-600 mb-1">Resolved</div>
            <div className="text-3xl font-bold text-green-600">
              {tickets.filter(t => t.status === 'resolved').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets List */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle>Tickets ({filteredTickets.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredTickets.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎫</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No tickets yet</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm ? 'No tickets match your search' : 'Click the floating help button to submit your first ticket'}
                </p>
                {!searchTerm && (
                  <div className="text-sm text-gray-500">
                    Or integrate via email, Slack, or Teams
                  </div>
                )}
              </div>
            ) : (
              filteredTickets.map((ticket) => (
                <Card 
                  key={ticket.id} 
                  className="border-gray-200 hover:border-cyan-300 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-sm font-mono text-gray-500">{ticket.ticket_id}</span>
                          <Badge className={getStatusColor(ticket.status)}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                          <Badge className={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                          {ticket.ai_handled && (
                            <Badge className="bg-cyan-100 text-cyan-800 border-cyan-200">
                              AI Resolved
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">{ticket.title}</h4>
                        <p className="text-sm text-gray-600 mb-2">{ticket.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>👤 {ticket.requester}</span>
                          <span>•</span>
                          <span>📂 {ticket.category}</span>
                          <span>•</span>
                          <span>👨‍💼 {ticket.assignee}</span>
                          <span>•</span>
                          <span>📅 {new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedTicket(null)}>
          <Card className="max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl">{selectedTicket.title}</CardTitle>
                  <CardDescription className="text-base mt-2">
                    Ticket ID: {selectedTicket.ticket_id}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSelectedTicket(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Badge className={getStatusColor(selectedTicket.status)}>
                  {selectedTicket.status.replace('_', ' ')}
                </Badge>
                <Badge className={getPriorityColor(selectedTicket.priority)}>
                  {selectedTicket.priority} priority
                </Badge>
              </div>
              
              <div>
                <div className="text-sm font-medium text-gray-900 mb-1">Description</div>
                <p className="text-gray-700">{selectedTicket.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-1">Requester</div>
                  <p className="text-gray-700">{selectedTicket.requester}</p>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-1">Assignee</div>
                  <p className="text-gray-700">{selectedTicket.assignee}</p>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-1">Category</div>
                  <p className="text-gray-700">{selectedTicket.category}</p>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-1">Created</div>
                  <p className="text-gray-700">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                </div>
              </div>

              {selectedTicket.resolution && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-green-900 mb-1">Resolution</div>
                  <p className="text-green-800">{selectedTicket.resolution}</p>
                  {selectedTicket.resolved_at && (
                    <p className="text-xs text-green-700 mt-2">
                      Resolved on {new Date(selectedTicket.resolved_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
